import { TrackedCTA } from 'components/CallToAction'
import Link from 'components/Link'
import Tooltip from 'components/Tooltip'
import useiCloudReady from '../../../hooks/useiCloudReady'
import React, { useState } from 'react'
import { BillingProductV2Type, IOtherOptionsSection, BillingV2FeatureType, BillingV2PlanType } from 'types'
import CheckIcon from '../../../images/check.svg'
import MinusIcon from '../../../images/x.svg'
import { PropertyManagement, AppLibrary, Websites, RealestateMarketing, RealestateCrm } from 'components/ProductIcons'
import { Platform, Support } from 'components/NotProductIcons'
import './styles/index.scss'
import Modal from 'components/Modal'
import { capitalizeFirstLetter } from '../../../utils/dateutils'
import Label from 'components/Label'
import { prepareData } from 'utils/googleData'

const icons = {
    property_management: <PropertyManagement />,
    realestate_crm: <RealestateCrm />,
    realestate_marketing: <RealestateMarketing />,
    websites: <Websites />,
    integrations: <AppLibrary />,
    platform_and_support: <Platform />,
    support: <Support />,
}
const convertLargeNumberToWords = (
    // The number to convert
    num: number | null,
    // The previous tier's number
    previousNum?: number | null,
    // Whether we will be showing multiple tiers (to denote the first tier with 'first')
    multipleTiers = false,
    // The product type (to denote the unit)
    productType: BillingProductV2Type['type'] | null = null
): string => {
    if (!num && previousNum) {
        return `${convertLargeNumberToWords(previousNum, null)} +`
    }
    if (!num) {
        return ''
    }

    let denominator = 1

    if (num >= 1000000) {
        denominator = 1000000
    } else if (num >= 1000) {
        denominator = 1000
    }

    return `${previousNum ? `${(previousNum / denominator).toFixed(0)}-` : multipleTiers ? 'first ' : ''}${(
        num / denominator
    ).toFixed(0)}${denominator === 1000000 ? ' million' : denominator === 1000 ? 'k' : ''}${
        !previousNum && multipleTiers ? ` ${productType}s/mo` : ''
    }`
}

export function PlanIcon({
    feature,
    timeDenominator,
}: {
    feature?: BillingV2FeatureType
    timeDenominator?: string
}): JSX.Element {
    return (
        <div className="flex items-center text-[15px]">
            {!feature ? (
                <>
                    <img src={MinusIcon} alt="Checked" className="text-red-500 h-5 w-5" aria-hidden="true" />
                    <span className="sr-only">Not included</span>
                </>
            ) : feature.limit && feature.limit != '' ? (
                <>
                    {feature.limit &&
                        feature.limit != '' &&
                        `${convertLargeNumberToWords(feature.limit, null)} ${feature.unit && feature.unit}${
                            timeDenominator ? `/${timeDenominator}` : ''
                        }`}
                    {feature.note}
                </>
            ) : (
                <>
                    {feature.note && feature.note != '' ? (
                        <>{feature.note}</>
                    ) : (
                        <>
                            <img src={CheckIcon} alt="Checked" className="text-green-500 h-5 w-5" aria-hidden="true" />
                            <span className="sr-only">Included in {feature.name}</span>
                        </>
                    )}
                </>
            )}
        </div>
    )
}

const ProductTiersModal = ({
    plan,
    modalOpen,
    setModalOpen,
    data,
}: {
    plan: BillingV2PlanType
    modalOpen: boolean
    setModalOpen: (open: boolean) => void
}): JSX.Element | null => {
    const tiers = plan?.tiers

    if (!plan || !tiers) {
        return null
    }
    const isFirstTierFree = parseFloat(tiers[0].unit_amount_usd) === 0
    const numberOfSigFigs = tiers.map((tier) => tier.unit_amount_usd.split('.')[1]?.length).sort((a, b) => b - a)[0]

    return (
        <Modal open={modalOpen} setOpen={setModalOpen}>
            <div className="flex h-full w-full items-center justify-center">
                <div className="relative m-8 w-full max-w-xl rounded-md bg-white p-8 text-left">
                    <p className="mb-1 text-gray">{capitalizeFirstLetter(plan.name)} pricing</p>
                    <p className="mb-1">
                        <span className="text-base font-bold">
                            $
                            {parseFloat(
                                isFirstTierFree ? tiers?.[1]?.unit_amount_usd : tiers?.[0]?.unit_amount_usd
                            ).toFixed(numberOfSigFigs)}
                        </span>
                        {/* the product types we have are plural, so we need to singularlize them and this works for now */}
                        <span className="text-gray">/{plan.unit ? plan.unit.replace(/s$/, '') : 'unit'}</span>
                    </p>
                    {isFirstTierFree && (
                        <p className="text-gray">
                            First {convertLargeNumberToWords(tiers[0].up_to)} {plan.unit}s/mo free
                        </p>
                    )}
                    <div>
                        <p className="text-gray">{data.text}</p>
                        <div className="grid grid-cols-2">
                            {tiers.map((tier, i) => {
                                return (
                                    <React.Fragment key={`tiers-modal-${plan.product_key}-tier-${i}`}>
                                        <p className="col-span-1 mb-0 border-b border-dashed border-gray-accent-light py-1">
                                            {convertLargeNumberToWords(
                                                tier.up_to,
                                                tiers[i - 1]?.up_to,
                                                true,
                                                plan.unit
                                            )}
                                        </p>
                                        <p className="col-span-1 mb-0 border-b border-dashed border-gray-accent-light py-1 font-bold">
                                            {isFirstTierFree && i === 0
                                                ? 'Free'
                                                : `$${parseFloat(tier.unit_amount_usd).toFixed(numberOfSigFigs)}`}
                                        </p>
                                    </React.Fragment>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

const ProductTiers = ({ plan, data }: { plan?: BillingV2PlanType }): JSX.Element => {
    const tiers = plan?.tiers
    const [modalOpen, setModalOpen] = useState(false)

    if (!plan || !tiers) {
        return (
            <>
                <span className="text-base font-bold">Free</span>
            </>
        )
    }
    return (
        <div>
            {tiers.map((tier, i) => {
                if ((parseFloat(tiers[0]?.unit_amount_usd) !== 0 && i > 0) || i > 1) {
                    return <React.Fragment key={plan.product_key + plan.plan_key + '-tiers-' + i}></React.Fragment>
                }

                return (
                    <div key={plan.product_key + plan.plan_key + '-tiers-' + i} className="pr-4">
                        {parseFloat(tier.unit_amount_usd) === 0 ? (
                            <div>
                                <span className="text-base font-bold">Free</span> up to{' '}
                                {convertLargeNumberToWords(tier.up_to, null, true, plan.unit)}, then
                            </div>
                        ) : (
                            <>
                                <span className="text-base font-bold">${parseFloat(tier.unit_amount_usd)}</span>
                                {/* the product types we have are plural, so we need to singularlize them and this works for now */}
                                <span className="text-gray">/{plan.unit ? plan.unit.replace(/s$/, '') : 'unit'}</span>
                                <p className="mb-0 text-sm">
                                    <Link to="" onClick={() => setModalOpen(true)} className="font-bold text-red">
                                        {data.text}
                                    </Link>{' '}
                                    after {convertLargeNumberToWords(tier.up_to)}/mo
                                    <ProductTiersModal
                                        modalOpen={modalOpen}
                                        setModalOpen={setModalOpen}
                                        plan={plan}
                                        data={data}
                                    />
                                </p>
                            </>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export const getPlanLimit = (plan?: BillingV2PlanType, result): JSX.Element => {
    if (!plan) return <></>
    if (!plan.free_allocation) {
        return <span className="font-bold">{result?.text2}</span>
    }
    return (
        <span className="font-bold">
            {convertLargeNumberToWords(plan.free_allocation)} {plan.unit}s<span className="font-normal ">/mo</span>
        </span>
    )
}

const AddonTag = () => {
    return <Label text="Addon" className="ml-2" />
}

const AddonTooltipContent = ({
    addon,
    parentProductName,
    data,
}: {
    addon: BillingProductV2Type
    parentProductName: string
}): JSX.Element => {
    const referencePlan = addon.plans?.[0]
    const tiers = referencePlan?.tiers
    const isFirstTierFree = parseFloat(tiers?.[0].unit_amount_usd || '') === 0
    const [modalOpen, setModalOpen] = useState(false)

    return (
        <div className="p-2">
            <p className="mb-2 text-[15px] font-bold">
                {addon.name} <AddonTag />
            </p>
            <p className="mb-3 text-sm">{addon.description}</p>
            <p className="text-sm ">
                {isFirstTierFree &&
                    `First ${convertLargeNumberToWords(tiers?.[0].up_to || null)} ${
                        referencePlan.unit
                    }s/mo free, then `}
                <span className="text-base font-bold text-black">
                    ${parseFloat((isFirstTierFree ? tiers?.[1]?.unit_amount_usd : tiers?.[0]?.unit_amount_usd) || '')}
                </span>
                {/* the product types we have are plural, so we need to singularlize them and this works for now */}
                <span className="">/{addon.unit ? addon.unit.replace(/s$/, '') : 'unit'} with</span>{' '}
                <Link to="" onClick={() => setModalOpen(true)} className="font-bold text-red">
                    volume discounts
                </Link>
                , billed on top of {parentProductName}.
                <ProductTiersModal modalOpen={modalOpen} setModalOpen={setModalOpen} plan={referencePlan} data={data} />
            </p>
        </div>
    )
}

export const PlanComparison = (props) => {
    const products = props.products
    const groupsToShow = props.groupsToShow
    const result = prepareData({ props })
    const { enable, title, subtitle, description, text, cta, editions, direction, showCTA } = result
    if ((enable === false || enable?.toLowerCase?.() === 'false') && !process.env.RENDER_DISABLED_COMPONENTS) {
        return <></>
    }
    const icloudready = useiCloudReady()
    const staticPlans = products?.[0]?.plans

    const inclusionOnlyProducts = products
        ?.filter((product) => product.inclusion_only == 'true')
        ?.map((product) => product.type)

    const excludedFeatures = [
        'ingestion_taxonomy',
        'terms_and_conditions',
        'security_assessment',
        'app_metrics',
        'paths_advanced',
    ]

    const tooltipPlacement = typeof window !== 'undefined' && window.innerWidth > 767 ? 'right' : 'bottom'

    return (
        <div dir={direction} className={`relative mb-0 -mt-8 w-full space-y-4 md:mt-0`}>
            {/* PLAN HEADERS */}
            <div className="sticky top-12 z-10 -mx-4 flex flex-wrap md:mx-0">
                <div
                    className={`flex-1 basis-[100%] border-b border-gray-accent-light bg-tan bg-opacity-95 py-2 pr-6 pb-4 pl-4 text-[14px] font-medium text-almost-black md:basis-0 md:pl-0`}
                >
                    <p className="mb-0 font-bold">{title}</p>
                    <p className="mb-0 text-sm ">{subtitle}</p>
                </div>

                <div className="flex w-full border-b border-gray-accent-light bg-tan/90 px-4 md:flex-[0_0_60%] md:gap-4">
                    {staticPlans?.length > 0 &&
                        staticPlans?.map((plan) => (
                            <div
                                dir={direction}
                                key={`${plan.plan_key}-header`}
                                className={`w-full border-l border-gray-accent-light/50 py-2 px-2 pb-4 text-sm  leading-tight text-almost-black first:border-l-0 md:border-0 md:pr-0 md:pl-0`}
                            >
                                <div className="flex h-full flex-1 flex-col justify-between">
                                    <div>
                                        {!groupsToShow ||
                                        !groupsToShow.some((product_name) =>
                                            inclusionOnlyProducts.includes(product_name)
                                        ) ? (
                                            <>
                                                <p className="mb-0 text-center font-bold ">
                                                    {plan.plan_key?.includes('professional')
                                                        ? editions?.[1]?.title
                                                        : editions?.[0]?.title}
                                                </p>
                                                <p className="mb-0 text-center font-bold ">
                                                    {plan.plan_key?.includes('professional')
                                                        ? editions?.[1]?.description
                                                        : editions?.[0]?.description}
                                                </p>
                                                <p className="mb-3 hidden text-sm  md:block">
                                                    {plan.plan_key?.includes('professional')
                                                        ? editions?.[1]?.text
                                                        : editions?.[0]?.text}
                                                </p>
                                            </>
                                        ) : groupsToShow.some((product_name) =>
                                              inclusionOnlyProducts.includes(product_name)
                                          ) ? (
                                            <>
                                                <div className="mb-2">
                                                    <p className="mb-0 text-center font-bold md:text-left">
                                                        {plan.free_allocation ? 'Free' : 'Included'}
                                                    </p>
                                                    {!plan.free_allocation && (
                                                        <p className="mb-3 hidden text-sm  md:block">
                                                            With any paid product plan
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                    {showCTA && (
                                        <TrackedCTA
                                            event={{
                                                name: `clicked Get started - free`,
                                                type: 'cloud',
                                            }}
                                            type="primary"
                                            size="sm"
                                            className="!w-auto shadow-md"
                                            to={`https://${
                                                icloudready?.isFeatureEnabled &&
                                                icloudready?.isFeatureEnabled('direct-to-saudi-cloud')
                                                    ? 'app'
                                                    : 'app'
                                            }.icloud-ready.com/register`}
                                        >
                                            Get started - free
                                        </TrackedCTA>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            </div>
            {/* PRODUCTS */}
            {products?.length > 0 &&
                products?.map((product) => {
                    if (groupsToShow && !groupsToShow.includes(product.type)) return null
                    // some products only have a paid plan, but we need to show something for the free plan, so we stub out values using this var
                    const stubMissingPlan = product?.plans?.length !== staticPlans.length
                    return (
                        <React.Fragment key={`product-${product.type}`}>
                            <div key={`product-${product.type}-feature-group-${product.type}`}>
                                <div className="flex flex-wrap">
                                    <div
                                        key={`${product.name}-group`}
                                        className={`-mx-4 flex-1 basis-[100%] justify-center pt-6 text-center text-primary md:mx-0 md:basis-0 md:pb-2 md:text-left`}
                                    >
                                        <h4 className="mb-0 flex w-full items-center justify-center gap-2 border-y border-gray-accent-light bg-gray-accent-light py-4 md:justify-start md:border-0 md:bg-transparent md:py-0">
                                            <span className="inline-block h-6 w-6">
                                                {/* {icons[`${product.type}`]} */}
                                                {/* <img
                                                src={product.image_url}
                                                alt={`logo for iCloudReady's ${product.name} product`}
                                            /> */}
                                            </span>{' '}
                                            {product.name}
                                        </h4>
                                    </div>
                                    <div className="flex w-full divide-x divide-gray-accent-light/50 px-4 md:flex-[0_0_60%] md:gap-4 md:divide-x-0">
                                        {product?.plans?.length > 0 &&
                                            product.plans?.map((plan, i) => (
                                                <React.Fragment
                                                    key={`${plan.plan_key}-${product.type}-free-allocation-or-limit`}
                                                >
                                                    {i === 0 && stubMissingPlan && <div className={`flex-1`} />}
                                                    <div
                                                        className={`flex-1 justify-center py-4 text-center md:pt-6 md:text-left`}
                                                    >
                                                        <div>{getPlanLimit(plan, result)}</div>
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                    </div>
                                </div>
                                {/* PRODUCT FEATURES & ADDONS */}
                                <div className="rounded bg-gray-accent-light/80 p-1 md:ml-6">
                                    {product.plans?.[product.plans.length - 1]?.features
                                        // don't include features that are in the excluded features list
                                        ?.filter((f) => !excludedFeatures.includes(f.key))
                                        ?.map((feature) => (
                                            <div
                                                className="rounded md:flex md:p-2 md:hover:bg-gray-accent/50"
                                                key={`${product.type}-subfeature-${feature.name}`}
                                            >
                                                <div
                                                    className={`flex-1 rounded bg-gray-accent/25 py-2 text-center md:bg-transparent md:py-0 md:text-left`}
                                                    key={`comparison-row-key-${feature.name}`}
                                                >
                                                    <Tooltip
                                                        content={() => (
                                                            <div className="p-2">
                                                                <p className="mb-1 text-[15px] font-bold">
                                                                    {feature.name}
                                                                </p>
                                                                <p className="mb-0 text-sm">{feature.description}</p>
                                                            </div>
                                                        )}
                                                        tooltipClassName="max-w-xs m-4"
                                                        placement={tooltipPlacement}
                                                    >
                                                        <span
                                                            className={`cursor-default border-b border-dashed border-gray-accent-light pb-0.5 text-[15px] font-bold`}
                                                        >
                                                            {feature.name}
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <div className="flex w-full divide-x divide-gray-accent-light/50 md:flex-[0_0_60%] md:gap-4 md:divide-x-0">
                                                    {product.plans.map((plan, i) => (
                                                        <React.Fragment key={`${plan.plan_key}-${feature.key}-value`}>
                                                            {i === 0 && stubMissingPlan ? (
                                                                <div
                                                                    className={`flex flex-1 justify-center py-4 md:justify-start md:border-none md:py-0 md:text-left`}
                                                                    key={`stubbed-plan-${feature.key}-value`}
                                                                >
                                                                    <PlanIcon feature={undefined} />
                                                                </div>
                                                            ) : null}
                                                            <div
                                                                className={`flex flex-1 justify-center py-4 md:justify-start md:border-none md:py-0 md:text-left`}
                                                            >
                                                                <PlanIcon
                                                                    feature={plan.features?.find(
                                                                        (f) => f.name === feature.name
                                                                    )}
                                                                />
                                                            </div>
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    {/* PRODUCT ADDONS */}
                                    {product?.addons?.length > 0 &&
                                        product.addons?.map((addon) => (
                                            <div
                                                className="rounded md:flex md:p-2 md:hover:bg-gray-accent/50"
                                                key={`${product.type}-addon-${addon.type}`}
                                            >
                                                <div
                                                    className={`flex-1 rounded bg-gray-accent/25 py-2 text-center md:bg-transparent md:py-0 md:text-left`}
                                                    key={`comparison-row-key-${addon.type}`}
                                                >
                                                    <Tooltip
                                                        content={() => (
                                                            <AddonTooltipContent
                                                                addon={addon}
                                                                parentProductName={product.name}
                                                                data={result}
                                                            />
                                                        )}
                                                        tooltipClassName="max-w-xs m-4"
                                                        placement={tooltipPlacement}
                                                    >
                                                        <span>
                                                            <span
                                                                className={`cursor-default border-b border-dashed border-gray-accent-light pb-0.5 text-[15px] font-bold`}
                                                            >
                                                                {addon.name}{' '}
                                                            </span>
                                                            <AddonTag />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <div className="flex w-full divide-x divide-gray-accent-light/50 md:flex-[0_0_60%] md:gap-4 md:divide-x-0">
                                                    {product?.plans?.length > 0 &&
                                                        product.plans.map((plan) => (
                                                            <div
                                                                className={`flex flex-1 justify-center py-4 md:justify-start md:border-none md:py-0 md:text-left`}
                                                                key={`${plan.plan_key}-${addon.type}-value`}
                                                            >
                                                                {plan.free_allocation ? (
                                                                    <PlanIcon />
                                                                ) : (
                                                                    <Tooltip
                                                                        content={() => (
                                                                            <AddonTooltipContent
                                                                                addon={addon}
                                                                                parentProductName={product.name}
                                                                                data={result}
                                                                            />
                                                                        )}
                                                                        tooltipClassName="max-w-xs m-4"
                                                                        placement={tooltipPlacement}
                                                                    >
                                                                        <span
                                                                            className={`pb-0.25 cursor-default border-b border-dashed border-gray-accent-light`}
                                                                        >
                                                                            Available
                                                                        </span>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                                {/* PRODUCT PRICING */}
                                {
                                    // If there is any plan with tiers, show the pricing
                                    product?.plans?.length > 0 &&
                                        product?.plans?.find((plan) => plan.tiers && plan.tiers?.length > 0) && (
                                            <div className="flex flex-wrap px-2 md:pl-8">
                                                <div
                                                    className={`hidden flex-1 basis-[100%] pt-4 text-center font-bold md:block md:basis-0 md:bg-transparent md:text-left`}
                                                >
                                                    {product.name} pricing
                                                </div>
                                                <div className="flex w-full md:flex-[0_0_60%]">
                                                    {product.plans?.map((plan) => (
                                                        <div
                                                            key={plan.plan_key + '-' + product.type + '-pricing'}
                                                            className={`flex-1 pl-2 pt-4 text-sm font-medium text-almost-black first:pl-0 md:border-none`}
                                                        >
                                                            <ProductTiers plan={plan} data={result} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                }
                            </div>
                        </React.Fragment>
                    )
                })}
            <div className="z-10 -mx-4 flex flex-wrap pb-6 md:mx-0">
                <div
                    className={`flex-1 basis-[100%] bg-tan bg-opacity-95 py-2 pr-6 pb-4 text-[14px] font-medium text-almost-black md:basis-0`}
                ></div>

                <div className="flex w-full bg-tan/90 px-4 md:flex-[0_0_60%] md:gap-4">
                    {staticPlans?.length > 0 &&
                        staticPlans.map((plan) => (
                            <div
                                key={`${plan.plan_key}-header`}
                                className={`w-full border-l border-gray-accent-light/50 py-2 px-2 pb-4 text-sm leading-tight text-almost-black first:border-l-0 md:border-0 md:pr-0 md:pl-0`}
                            >
                                <div className="flex h-full flex-1 flex-col justify-between">
                                    <div>
                                        {!groupsToShow ? (
                                            <p className="mb-2 text-center font-bold md:text-left">
                                                {plan.free_allocation ? editions?.[0]?.title : editions?.[1]?.title}
                                            </p>
                                        ) : groupsToShow.some((product_name) =>
                                              inclusionOnlyProducts.includes(product_name)
                                          ) ? (
                                            <>
                                                <div className="mb-2">
                                                    <p className="mb-0 text-center font-bold md:text-left">
                                                        {plan.free_allocation ? 'Free' : 'Included'}
                                                    </p>
                                                    {!plan.free_allocation && (
                                                        <p className="text-sm ">With any paid product plan</p>
                                                    )}
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                    {showCTA && (
                                        <TrackedCTA
                                            event={{
                                                name: `clicked Get started - free`,
                                                type: 'cloud',
                                            }}
                                            type="primary"
                                            size="sm"
                                            className="!w-auto shadow-md"
                                            to={`https://${
                                                icloudready?.isFeatureEnabled &&
                                                icloudready?.isFeatureEnabled('direct-to-saudi-cloud')
                                                    ? 'app'
                                                    : 'app'
                                            }.icloud-ready.com/register`}
                                        >
                                            Get started - free
                                        </TrackedCTA>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    )
}
